import React, { useState, useEffect } from "react";
import {
    Search,
    Users,
    Clock,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Eye,
    EyeOff
} from "lucide-react";
import { EnrollmentApi } from "../services/enrollment.service";
import ComplianceDocsPanel from "./ComplianceDocsPanel";

interface EnrollmentListProps {
    status: "IN_PROGRESS" | "SUBMITTED";
    campaignId?: string;
    campaignName?: string;
}

export default function EnrollmentList({ status, campaignId, campaignName }: EnrollmentListProps) {
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const result = await EnrollmentApi.listSessions({
                status: status === "IN_PROGRESS" ? "IN_PROGRESS,PENDING,SIGNED" : "SUBMITTED,COMPLETED",
                campaignId,
                search: searchTerm || undefined,
                page: pagination.page,
                limit: pagination.limit
            });
            setSessions(result.sessions);
            if (result.pagination) {
                setPagination(prev => ({
                    ...prev,
                    total: result.pagination!.total,
                    totalPages: result.pagination!.totalPages
                }));
            }
        } catch (error) {
            console.error("Error fetching sessions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [status, campaignId, pagination.page]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSessions();
    };

    const getClientName = (session: any) => {
        const data = session.data || {};
        return (
            (data.intakeContact?.intakeFirstName && data.intakeContact?.intakeLastName
                ? `${data.intakeContact.intakeFirstName} ${data.intakeContact.intakeLastName}`
                : data.injuredParty?.injuredPartyFirstName && data.injuredParty?.injuredPartyLastName
                    ? `${data.injuredParty.injuredPartyFirstName} ${data.injuredParty.injuredPartyLastName}`
                    : data.clientName || "-")
        );
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "IN_PROGRESS":
                return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">In Progress</span>;
            case "PENDING":
                return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>;
            case "SIGNED":
                return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Signed</span>;
            case "SUBMITTED":
                return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Submitted</span>;
            case "COMPLETED":
                return <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-800 rounded-full">Completed</span>;
            default:
                return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${status === 'IN_PROGRESS' ? 'bg-blue-100' : 'bg-green-100'}`}>
                            {status === 'IN_PROGRESS' ? (
                                <Clock className="w-6 h-6 text-blue-600" />
                            ) : (
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {status === 'IN_PROGRESS' ? 'In Progress Applications' : 'Submitted Applications'}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {campaignName ? `Campaign: ${campaignName}` : 'All Campaigns'}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-500 font-medium whitespace-nowrap">Total Records</p>
                        <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <form onSubmit={handleSearch} className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by client name, email or session ID..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </form>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2">
                                            <Clock className="w-8 h-8 animate-spin text-blue-500" />
                                            <p>Loading applications...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : sessions.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        No applications found
                                    </td>
                                </tr>
                            ) : (
                                sessions.map((session) => (
                                    <React.Fragment key={session.id}>
                                        <tr className={`hover:bg-gray-50 transition-colors ${expandedRow === session.id ? 'bg-blue-50/30' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-medium">
                                                        {getClientName(session).charAt(0) || '?'}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{getClientName(session)}</div>
                                                        <div className="text-xs text-gray-500">{session.data?.intakeContact?.clientEmail || session.data?.intakeContact?.intakeEmail || '-'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(session.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <Users className="w-4 h-4" />
                                                    {session.agent ? `${session.agent.firstName} ${session.agent.lastName}` : 'Unknown'}
                                                </div>
                                                {session.agent?.agentProfile?.agentCode && (
                                                    <div className="text-xs text-gray-400 ml-6">{session.agent.agentProfile.agentCode}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {session.campaign?.name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(session.updatedAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <button
                                                    onClick={() => setExpandedRow(expandedRow === session.id ? null : session.id)}
                                                    className={`p-2 rounded-lg transition-colors ${expandedRow === session.id
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                                        }`}
                                                    title={expandedRow === session.id ? "Close Details" : "View Details"}
                                                >
                                                    {expandedRow === session.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRow === session.id && (
                                            <tr>
                                                <td colSpan={6} className="p-0 border-b border-gray-200">
                                                    <div className="animate-in slide-in-from-top-4 duration-300">
                                                        <ComplianceDocsPanel sessionId={session.id} />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
                        <div className="text-sm text-gray-500">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                disabled={pagination.page === 1}
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                                disabled={pagination.page === pagination.totalPages}
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
